import { Button, Grid, useIsMobile, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { OverridableInputField, useClientOptions } from '@paintscout/ui-business'
import type { UpdateableArea, UpdateableAreaSubstrate } from '@paintscout/util/builder'
import { getFeatures, getObjectLabels } from '@paintscout/util/builder'
import React from 'react'
import SwapIcon from '@material-ui/icons/Cached'
import type { QuoteDocument } from 'paintscout'

const DEFAULT_VALUE_PREFIX = 'Default Value:'

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    [theme.breakpoints.down('xs')]: {
      paddingBottom: '0 !important'
    }
  },
  swapButton: {
    marginTop: theme.spacing(3.5),
    [theme.breakpoints.down('xs')]: {
      margin: 0
    }
  }
}))

interface DimensionOverridesProps {
  quote: QuoteDocument
  areaSubstrate: UpdateableAreaSubstrate
  area: UpdateableArea
  rate: UpdateableAreaSubstrate['rate']
  onChange: (name: string) => (value: any) => void
  disabled: boolean
}

export default function DimensionOverrides({
  quote,
  areaSubstrate,
  area,
  rate,
  onChange,
  disabled
}: DimensionOverridesProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { unit: unitLabel } = getObjectLabels({ options })
  const mobile = useIsMobile()
  const features = getFeatures({ options })
  const isTemplate = quote.type === 'quoteTemplate'

  const dimensionLabel = unitLabel.value === 'Metre' ? 'm' : areaSubstrate?.useInches ? 'IN' : 'FT'
  const invalidLength = areaSubstrate.length?.useCustom && areaSubstrate.length?.custom <= 0
  const invalidWidth = areaSubstrate.width?.useCustom && areaSubstrate.width?.custom <= 0
  const invalidHeight = areaSubstrate.height?.useCustom && areaSubstrate.height?.custom <= 0
  const invalidPrefix = 'Invalid value, using default:'

  const showSwitchButton = features.quotes?.areaSubstrates?.showSwitchButton

  const handleSwap = () => {
    onChange('showOverrideDimensions')(!areaSubstrate.showOverrideDimensions)
  }

  if (rate.rateType === 'lnft') {
    if (areaSubstrate.showOverrideDimensions && area.areaType === 'room' && showSwitchButton) {
      return (
        <>
          <Grid item xs={12} sm={3}>
            <OverridableInputField
              label={`${unitLabel.abbreviation.linear} (${dimensionLabel})`}
              format={'dimension'}
              value={areaSubstrate.dimensionOverride}
              onChange={onChange('dimensionOverride')}
              fullWidth={true}
              resetBlank={true}
              autoSelect={true}
              disabled={disabled}
            />
          </Grid>
          <Grid item className={classes.buttonContainer}>
            <Button
              onClick={handleSwap}
              endIcon={<SwapIcon />}
              variant="text"
              size={mobile ? 'medium' : 'small'}
              className={classes.swapButton}
              disabled={isTemplate}
            >
              switch to L/W
            </Button>
          </Grid>
        </>
      )
    }
    return (
      <>
        <Grid item xs={6} sm={3}>
          <OverridableInputField
            label={`Length (${dimensionLabel})`}
            name={'length'}
            format={'dimension'}
            value={areaSubstrate.length}
            onChange={onChange('length')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
            helpPrefix={invalidLength ? invalidPrefix : DEFAULT_VALUE_PREFIX}
          />
        </Grid>
        {area.areaType === 'room' && (
          <Grid item xs={6} sm={3}>
            <OverridableInputField
              label={`Width (${dimensionLabel})`}
              name={'width'}
              format={'dimension'}
              value={areaSubstrate.width}
              onChange={onChange('width')}
              fullWidth={true}
              resetBlank={true}
              autoSelect={true}
              disabled={disabled}
              helpPrefix={invalidWidth ? invalidPrefix : DEFAULT_VALUE_PREFIX}
            />
          </Grid>
        )}
        {area.areaType === 'room' && showSwitchButton && (
          <Grid item className={classes.buttonContainer}>
            <Button
              onClick={handleSwap}
              endIcon={<SwapIcon />}
              variant="text"
              size={mobile ? 'medium' : 'small'}
              className={classes.swapButton}
              disabled={isTemplate}
            >
              switch to {unitLabel.abbreviation.linear}
            </Button>
          </Grid>
        )}
      </>
    )
  } else if (rate.rateType === 'cuft' || (area.areaType === 'room' && rate.rateType === 'sqftWalls')) {
    if (areaSubstrate.showOverrideDimensions && showSwitchButton) {
      return (
        <>
          <Grid item xs={12} sm={3}>
            <OverridableInputField
              label={`${unitLabel.abbreviation.square} (${dimensionLabel})`}
              format={'dimension'}
              value={areaSubstrate.dimensionOverride}
              onChange={onChange('dimensionOverride')}
              fullWidth={true}
              resetBlank={true}
              autoSelect={true}
              disabled={disabled}
            />
          </Grid>
          <Grid item className={classes.buttonContainer}>
            <Button
              onClick={handleSwap}
              endIcon={<SwapIcon />}
              variant="text"
              size={mobile ? 'medium' : 'small'}
              className={classes.swapButton}
              disabled={isTemplate}
            >
              switch to L/W/H
            </Button>
          </Grid>
        </>
      )
    }
    return (
      <>
        <Grid item xs={4} sm={2}>
          <OverridableInputField
            label={`Length (${dimensionLabel})`}
            name={'length'}
            format={'dimension'}
            value={areaSubstrate.length}
            onChange={onChange('length')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
            helpPrefix={invalidLength ? invalidPrefix : DEFAULT_VALUE_PREFIX}
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <OverridableInputField
            label={`Width (${dimensionLabel})`}
            name={'width'}
            format={'dimension'}
            value={areaSubstrate.width}
            onChange={onChange('width')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
            helpPrefix={invalidWidth ? invalidPrefix : DEFAULT_VALUE_PREFIX}
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <OverridableInputField
            label={`Height (${dimensionLabel})`}
            name={'height'}
            format={'dimension'}
            value={areaSubstrate.height}
            onChange={onChange('height')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
            helpPrefix={invalidHeight ? invalidPrefix : DEFAULT_VALUE_PREFIX}
          />
        </Grid>
        {showSwitchButton && (
          <Grid item className={classes.buttonContainer}>
            <Button
              onClick={handleSwap}
              endIcon={<SwapIcon />}
              variant="text"
              size={mobile ? 'medium' : 'small'}
              className={classes.swapButton}
              disabled={isTemplate}
            >
              switch to {unitLabel.abbreviation.square}
            </Button>
          </Grid>
        )}
      </>
    )
  }
  if (areaSubstrate.showOverrideDimensions && showSwitchButton) {
    return (
      <>
        <Grid item xs={12} sm={3}>
          <OverridableInputField
            label={`${unitLabel.abbreviation.square} (${dimensionLabel})`}
            format={'dimension'}
            value={areaSubstrate.dimensionOverride}
            onChange={onChange('dimensionOverride')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
          />
        </Grid>
        <Grid item className={classes.buttonContainer}>
          <Button
            onClick={handleSwap}
            endIcon={<SwapIcon />}
            variant="text"
            size={mobile ? 'medium' : 'small'}
            className={classes.swapButton}
            disabled={isTemplate}
          >
            switch to L/W
          </Button>
        </Grid>
      </>
    )
  }
  return (
    <>
      <Grid item xs={6} sm={3}>
        <OverridableInputField
          label={`Length (${dimensionLabel})`}
          name={'length'}
          format={'dimension'}
          value={areaSubstrate.length}
          onChange={onChange('length')}
          fullWidth={true}
          resetBlank={true}
          autoSelect={true}
          disabled={disabled}
          helpPrefix={invalidLength ? invalidPrefix : DEFAULT_VALUE_PREFIX}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        {area.areaType === 'room' && (
          <OverridableInputField
            label={`Width (${dimensionLabel})`}
            name={'width'}
            format={'dimension'}
            value={areaSubstrate.width}
            onChange={onChange('width')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
            helpPrefix={invalidWidth ? invalidPrefix : DEFAULT_VALUE_PREFIX}
          />
        )}
        {area.areaType !== 'room' && (
          <OverridableInputField
            label={`Width or Height (${dimensionLabel})`}
            name={'height'}
            format={'dimension'}
            value={areaSubstrate.height}
            onChange={onChange('height')}
            fullWidth={true}
            resetBlank={true}
            autoSelect={true}
            disabled={disabled}
            helpPrefix={invalidHeight ? invalidPrefix : DEFAULT_VALUE_PREFIX}
          />
        )}
      </Grid>
      {showSwitchButton && (
        <Grid item className={classes.buttonContainer}>
          <Button
            onClick={handleSwap}
            endIcon={<SwapIcon />}
            variant="text"
            size={mobile ? 'medium' : 'small'}
            className={classes.swapButton}
            disabled={isTemplate}
          >
            switch to {unitLabel.abbreviation.square}
          </Button>
        </Grid>
      )}
    </>
  )
}
